import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  useId,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FilterPopup } from './FilterPopup';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useDiscoveryMode, useDiscoveryOptional } from '../../contexts/discoveryStore';
import { loadRegistry, registryCityToItem } from '../../utils/registryLookup';
import { nextDiscoveryUrl, territoryPickUrl } from '../../utils/discoveryFilters';
import { placeByGps, acquireGpsFix } from '../../utils/gpsPlacement';
import { useComboboxKeyboard } from './useComboboxKeyboard';
import { ComuneSuggestionList } from './ComuneSuggestionList';
import type { CityItem, LocationChoice } from '../../contexts/UserContext';
import type { RegistryCityItem, ResolvedMunicipality } from '@buzzmap/shared';
import styles from './LocalitaPopup.module.css';

/**
 * Località control (T015-FE).
 *
 * TWO lanes, gated on the discovery mode:
 *
 * - `legacy` (pre-activation production) keeps today's behaviour byte-for-byte:
 *   a MULTI-city selection capped by the manual limit, a local draft that
 *   commits live as a v1 `LocationChoice`, and the v1 radius commit.
 * - `v2` (registry active) is a SINGLE-comune picker: selection goes through
 *   `setMunicipality(municipalityId)` by current UUID — replace, never append —
 *   the radius slider drives the URL through the T012 facade (plus the
 *   independent saved radius), GPS runs the one shared placement path
 *   (`placeByGps`), picking a comune (search, GPS, candidate) saves the preference
 *   AND navigates to its territorial URL (the URL is the active authority), and
 *   "Tutta Italia" fires `explicitItalia` + a national URL.
 *
 * Removed here (T015 acceptance "no legacy popup props/GPS implementation/
 * coordinate stub fallback"): the deprecated `onApply`/`initialValues`/
 * `onDismiss`/`bboxActive` props (their only caller passed props whose apply
 * hook has been a documented no-op since the live-commit migration) and the
 * component's own `navigator.geolocation.getCurrentPosition` — acquisition now
 * always goes through `utils/gpsPlacement`.
 *
 * The manual 20-city limit is kept for the legacy lane ONLY and mirrors the
 * API's per-request cap; it is deliberately unrelated to the territory
 * resolution's center count (a region's 12 centers can never become a
 * 12-city preference, plan l.69).
 */
export interface LocalitaPopupProps {
  open: boolean;
  /**
   * Called when the popup is dismissed (backdrop / Esc / Tutta Italia).
   * No commit is required on close — state is committed live.
   */
  onClose?: () => void;
  /**
   * Optional metadata callback fired AFTER a live commit has been written
   * to user preferences. Most callers can omit this.
   */
  onChange?: (location: LocationChoice) => void;
  /**
   * Optional anchor element. When provided AND viewport is desktop (>=1024px),
   * the underlying FilterPopup renders as a fixed-position popover anchored
   * below the element. On mobile or when omitted, falls back to bottom sheet.
   * Forwarded as-is to FilterPopup (which gates the popover variant internally).
   */
  anchorEl?: HTMLElement | null;
}

const DEFAULT_RADIUS_KM = 25;
/**
 * Mirrors MAX_CENTERS in api/events/index.ts: each LEGACY selected city
 * becomes one center, and the API answers 400 past that cap. Change both
 * together. Not used in v2 (one comune, no manual list).
 */
const MAX_SELECTED_CITIES = 20;
const RADIUS_DEBOUNCE_MS = 300;
/** User-facing copy for the shared GPS placement outcomes (v2 lane). */
const GPS_AMBIGUOUS_MESSAGE =
  'Posizione incerta: scegli il tuo comune dall’elenco, oppure riprova con la posizione attuale.';
const GPS_UNAVAILABLE_MESSAGE =
  'Posizione non disponibile: scegli il tuo comune, oppure riprova con la posizione attuale.';

function LocationIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="5" />
      <circle cx="9" cy="9" r="1.5" />
      <line x1="9" y1="1" x2="9" y2="3" />
      <line x1="9" y1="15" x2="9" y2="17" />
      <line x1="1" y1="9" x2="3" y2="9" />
      <line x1="15" y1="9" x2="17" y2="9" />
    </svg>
  );
}

/** One selected-city chip with its remove control (both lanes render this row). */
function SelectedCityChip({
  name,
  province,
  onRemove,
}: {
  name: string;
  province?: string;
  onRemove: () => void;
}) {
  return (
    <span className={styles.chip}>
      <span>
        {name}
        {province && ` (${province})`}
      </span>
      <button
        type="button"
        className={styles.chipRemove}
        onClick={onRemove}
        aria-label={`Rimuovi ${name}`}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <line x1="2" y1="2" x2="8" y2="8" />
          <line x1="8" y1="2" x2="2" y2="8" />
        </svg>
      </button>
    </span>
  );
}

/** Draft seed for the LEGACY lane, from the v1 location only. */
function deriveInitialDraft(location: LocationChoice): {
  cities: CityItem[];
  radiusKm: number;
  gpsActive: boolean;
  gpsCoords?: [number, number];
} {
  if (location.type === 'cities') {
    return { cities: location.cities, radiusKm: location.radiusKm, gpsActive: false };
  }
  if (location.type === 'gps') {
    return {
      cities: [],
      radiusKm: location.radiusKm,
      gpsActive: true,
      gpsCoords: location.coords,
    };
  }
  return { cities: [], radiusKm: DEFAULT_RADIUS_KM, gpsActive: false };
}

export function LocalitaPopup({ open, onClose, onChange, anchorEl }: LocalitaPopupProps) {
  const {
    setLocationChoice,
    clearLocation,
    setMunicipality,
    setSavedRadiusKm,
    effectiveLocation,
    preferences,
    preferencesV2,
    locationNeedsConfirmation,
    prefsSyncNotice,
    dismissPrefsSyncNotice,
  } = useUserPreferences();

  const mode = useDiscoveryMode();
  const v2 = mode === 'v2';
  const discovery = useDiscoveryOptional();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Async picks navigate after an await: read the URL as it is THEN, not at click time.
  const routerLocationRef = useRef(routerLocation);
  routerLocationRef.current = routerLocation;

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const [allCities, setAllCities] = useState<RegistryCityItem[]>([]);
  const [registryState, setRegistryState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle'
  );
  const [citySearch, setCitySearch] = useState('');

  const [draftCities, setDraftCities] = useState<CityItem[]>([]);
  const [draftRadius, setDraftRadius] = useState(DEFAULT_RADIUS_KM);
  const [draftGps, setDraftGps] = useState(false);
  const [draftGpsCoords, setDraftGpsCoords] = useState<[number, number] | undefined>(undefined);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  /** v2: still-ambiguous containment candidates → manual choice, never a guess. */
  const [gpsCandidates, setGpsCandidates] = useState<ResolvedMunicipality[]>([]);

  // Debounce ref for radius slider live commits
  const radiusDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Bumped by every manual action so a late GPS result can never apply (T011 S6). */
  const gpsRunRef = useRef(0);
  /** One registry request per popup-open (the module cache dedupes the rest). */
  const openedRef = useRef(false);

  // Cleanup any pending debounce on unmount
  useEffect(() => {
    return () => {
      if (radiusDebounceRef.current !== null) {
        clearTimeout(radiusDebounceRef.current);
        radiusDebounceRef.current = null;
      }
    };
  }, []);

  const fetchCities = useCallback(() => {
    setRegistryState('loading');
    loadRegistry()
      .then((body) => {
        setAllCities(body.cities ?? []);
        setRegistryState('ready');
      })
      .catch(() => {
        // Registry fault → error + retry. No static-city fallback, no
        // fabricated center (T015 acceptance).
        setRegistryState('error');
      });
  }, []);

  useEffect(() => {
    if (!open) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;
    if (registryState !== 'ready') fetchCities();
  }, [open, registryState, fetchCities]);

  // Transient UI state resets each time the popup opens. useLayoutEffect so
  // the reset lands BEFORE paint — an `open` flag flipped in a passive effect
  // would show the previous state for one frame (same reason as the seed below).
  useLayoutEffect(() => {
    if (!open) return;
    setCitySearch('');
    setGpsError(null);
    setGpsLoading(false);
    setGpsCandidates([]);
  }, [open]);

  // Draft state re-derives whenever the popup opens or its authority changes
  // while open (v2: the independent saved radius / the URL's active radius;
  // legacy: the v1 location). Dragging is safe: the draft only changes on a
  // commit, which lands with the same value the user just dragged to.
  //
  // S7 (review): useLayoutEffect, not useEffect — the slider must never paint
  // the DEFAULT_RADIUS_KM (25) before the saved/active radius applies (HEAD
  // seeded this state during the first render instead).
  useLayoutEffect(() => {
    if (!open) return;
    if (v2) {
      setDraftRadius(
        effectiveLocation.type !== 'none'
          ? effectiveLocation.radiusKm
          : (preferencesV2?.radiusKm ?? DEFAULT_RADIUS_KM)
      );
      setDraftCities([]);
      setDraftGps(false);
      setDraftGpsCoords(undefined);
      return;
    }
    const seed = deriveInitialDraft(effectiveLocation);
    setDraftCities(seed.cities);
    setDraftRadius(seed.radiusKm);
    setDraftGps(seed.gpsActive);
    setDraftGpsCoords(seed.gpsCoords);
  }, [open, v2, preferencesV2, effectiveLocation]);

  /** Legacy live-commit funnel: the v1 `LocationChoice` lane. */
  const commit = useCallback(
    (choice: LocationChoice) => {
      if (choice.type === 'none') clearLocation();
      else setLocationChoice(choice);
      onChange?.(choice);
    },
    [clearLocation, setLocationChoice, onChange]
  );

  // ----- City selection (live) -----

  /** A manual pick supersedes any in-flight GPS result and resets the search UI. */
  const beginManualChoice = useCallback(() => {
    gpsRunRef.current += 1;
    setCitySearch('');
    setGpsError(null);
    setGpsCandidates([]);
  }, []);

  /**
   * v2: apply ONE comune by current UUID — REPLACE, never append. The commit
   * is only reported when the context actually applied it (an unknown id or an
   * unreachable registry keeps the previous identity and shows a notice).
   */
  /**
   * v2: the URL is the sole active authority, so a saved preference alone never
   * moves the feed. Once the preference is applied, navigate to the comune's
   * territorial URL (filters reset like a Home link — see `territoryPickUrl`)
   * and close the sheet, mirroring "Tutta Italia". A non-discovery URL or a
   * comune without a path keeps the preference only.
   */
  const activateComune = useCallback(
    (path: string | null | undefined) => {
      if (path) {
        const { pathname, search } = routerLocationRef.current;
        const target = territoryPickUrl({ pathname, search }, path, draftRadius);
        if (target && target !== `${pathname}${search}`) navigate(target);
      }
      handleClose();
    },
    [draftRadius, navigate, handleClose]
  );

  const handlePickComune = useCallback(
    (municipalityId: string, item: CityItem, path: string | null | undefined) => {
      beginManualChoice();
      void setMunicipality(municipalityId, draftRadius).then((applied) => {
        if (!applied) return;
        onChange?.({ type: 'cities', cities: [item], radiusKm: draftRadius });
        activateComune(path);
      });
    },
    [beginManualChoice, setMunicipality, draftRadius, onChange, activateComune]
  );

  const handleSelectCity = useCallback(
    (city: RegistryCityItem) => {
      if (v2) {
        handlePickComune(city.id, registryCityToItem(city), city.path);
        return;
      }
      beginManualChoice();

      const item = registryCityToItem(city);
      const exists = draftCities.some((c) => c.name === item.name);
      if (!exists && draftCities.length >= MAX_SELECTED_CITIES) return;
      const nextCities = exists ? draftCities : [...draftCities, item];
      setDraftCities(nextCities);
      setDraftGps(false);
      setDraftGpsCoords(undefined);
      commit({ type: 'cities', cities: nextCities, radiusKm: draftRadius });
    },
    [v2, handlePickComune, beginManualChoice, draftCities, draftRadius, commit]
  );

  /** v2: drop the single saved comune (radius and favorites keep their value). */
  const handleClearComune = useCallback(() => {
    gpsRunRef.current += 1;
    clearLocation();
    onChange?.({ type: 'none' });
  }, [clearLocation, onChange]);

  const handleRemoveCity = useCallback(
    (cityName: string) => {
      const nextCities = draftCities.filter((c) => c.name !== cityName);
      setDraftCities(nextCities);
      if (nextCities.length === 0) {
        commit({ type: 'none' });
      } else {
        commit({ type: 'cities', cities: nextCities, radiusKm: draftRadius });
      }
    },
    [draftCities, draftRadius, commit]
  );
  /**
   * "Tutta Italia" — the explicit-Italia UI trigger T010/T013 left open
   * (plan l.134): persists an explicit `none`, cancels pending discovery work,
   * clears the visit fallback and lands on the national URL. The entry gate
   * sees `explicitNationalRef` first, so `/` is NOT replaced by the saved or
   * last-visited territory. Filters are dropped — same reset semantics as a
   * Home link (plan l.64).
   */
  const handleTuttaItalia = useCallback(() => {
    gpsRunRef.current += 1;
    setDraftCities([]);
    setDraftGps(false);
    setDraftGpsCoords(undefined);
    commit({ type: 'none' });
    if (v2) {
      discovery?.explicitItalia();
      if (routerLocation.pathname !== '/') navigate('/');
    }
    handleClose();
  }, [commit, v2, discovery, routerLocation.pathname, navigate, handleClose]);

  /**
   * GPS (T015): ONE acquisition path, no `navigator.geolocation` here.
   * - v2: `placeByGps` (acquire → containment → one high-accuracy retry) and
   *   the resolved comune by UUID; an uncertain/unavailable outcome keeps the
   *   current selection and offers manual recovery (candidates / the picker).
   * - legacy: the same shared acquisition feeding today's v1 `gps` commit.
   */
  const handleGpsClick = useCallback(async () => {
    if (gpsLoading) return;
    setGpsLoading(true);
    setGpsError(null);
    setGpsCandidates([]);
    const run = ++gpsRunRef.current;
    try {
      if (v2) {
        const outcome = await placeByGps();
        if (gpsRunRef.current !== run) return; // superseded by a manual choice
        if (outcome.kind === 'resolved') {
          const m = outcome.municipality;
          const applied = await setMunicipality(m.id, draftRadius);
          if (applied) {
            onChange?.({
              type: 'cities',
              cities: [{ name: m.name, lat: m.center.lat, lng: m.center.lng, province: m.province }],
              radiusKm: draftRadius,
            });
            activateComune(m.path);
          }
          return;
        }
        if (outcome.kind === 'ambiguous') {
          setGpsCandidates(outcome.candidates);
          setGpsError(GPS_AMBIGUOUS_MESSAGE);
          return;
        }
        setGpsError(GPS_UNAVAILABLE_MESSAGE);
        return;
      }

      const attempt = await acquireGpsFix('fast');
      if (gpsRunRef.current !== run) return;
      if (attempt.kind === 'error') {
        setGpsError('Geolocalizzazione non disponibile');
        return;
      }
      const coords: [number, number] = [attempt.fix.lat, attempt.fix.lng];
      setDraftCities([]);
      setDraftGps(true);
      setDraftGpsCoords(coords);
      commit({ type: 'gps', coords, radiusKm: draftRadius, ts: Date.now() });
    } finally {
      setGpsLoading(false);
    }
  }, [gpsLoading, v2, setMunicipality, draftRadius, onChange, commit, activateComune]);

  // ----- Radius slider (debounced commit) -----

  /**
   * v2 radius commit. Active radius = URL (the T012 facade's exact action,
   * through the same codec; the facade itself needs an EventsProvider the
   * popup must not require). Saved radius = the independent sibling field
   * (plan l.69), never the URL's business. With no active territory there is
   * no URL to own a radius — only the saved one moves, so a generic entry can
   * never be re-targeted by this drag.
   */
  const commitV2Radius = useCallback(
    (next: number) => {
      if (effectiveLocation.type !== 'none') {
        const target = nextDiscoveryUrl(
          { pathname: routerLocation.pathname, search: routerLocation.search },
          { radiusKm: next }
        );
        if (target && target !== `${routerLocation.pathname}${routerLocation.search}`) {
          navigate(target);
        }
      }
      setSavedRadiusKm(next);
    },
    [
      effectiveLocation.type,
      routerLocation.pathname,
      routerLocation.search,
      navigate,
      setSavedRadiusKm,
    ]
  );

  const handleRadiusChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = Number(e.target.value);
      setDraftRadius(next);

      if (radiusDebounceRef.current !== null) {
        clearTimeout(radiusDebounceRef.current);
      }

      radiusDebounceRef.current = setTimeout(() => {
        radiusDebounceRef.current = null;
        if (v2) {
          commitV2Radius(next);
          return;
        }
        // Legacy: commit using the CURRENT location type (unchanged).
        if (draftGps && draftGpsCoords) {
          commit({ type: 'gps', coords: draftGpsCoords, radiusKm: next, ts: Date.now() });
        } else if (draftCities.length > 0) {
          commit({ type: 'cities', cities: draftCities, radiusKm: next });
        }
        // If neither cities nor gps is active, radius alone has no effect.
      }, RADIUS_DEBOUNCE_MS);
    },
    [v2, commitV2Radius, draftGps, draftGpsCoords, draftCities, commit]
  );

  const atCityLimit = draftCities.length >= MAX_SELECTED_CITIES;
  const savedComuneId =
    preferencesV2?.location.type === 'comune' ? preferencesV2.location.municipalityId : null;
  /** v2 chip: exactly ONE row for the saved id — the old list can never resurface. */
  const selectedRow = savedComuneId ? (allCities.find((c) => c.id === savedComuneId) ?? null) : null;
  const projectedCity =
    preferences.location.type === 'cities' ? preferences.location.cities[0] : undefined;
  const v2Chip: CityItem | null = !savedComuneId
    ? null
    : selectedRow
      ? registryCityToItem(selectedRow)
      : (projectedCity ?? null);

  const suggestions = useMemo(() => {
    const query = citySearch.trim().toLowerCase();
    if (query.length < 2) return [];
    if (!v2 && draftCities.length >= MAX_SELECTED_CITIES) return [];

    const matches = allCities.filter((c) => c.name.toLowerCase().includes(query));
    matches.sort((a, b) => {
      const aStarts = a.name.toLowerCase().startsWith(query);
      const bStarts = b.name.toLowerCase().startsWith(query);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.name.localeCompare(b.name);
    });

    if (v2) {
      return matches.filter((c) => c.id !== savedComuneId).slice(0, 10);
    }
    const selectedNames = new Set(draftCities.map((c) => c.name));
    return matches.filter((c) => !selectedNames.has(c.name)).slice(0, 10);
  }, [allCities, citySearch, draftCities, v2, savedComuneId]);

  // S11: per-instance id — a module constant could collide with a
  // `ComunePicker id="city"` (`city-suggestions`) if both ever mounted.
  const suggestionsListId = useId();
  const combobox = useComboboxKeyboard({
    count: suggestions.length,
    listId: suggestionsListId,
    onSelect: (index) => {
      const city = suggestions[index];
      if (city) handleSelectCity(city);
    },
  });

  const sliderFillPercent = ((draftRadius - 5) / (200 - 5)) * 100;

  const registryLoading = registryState === 'idle' || registryState === 'loading';
  const registryError = registryState === 'error';

  /** v2: pick one of the still-ambiguous containment candidates (manual recovery). */
  const handlePickCandidate = useCallback(
    (m: ResolvedMunicipality) => {
      handlePickComune(
        m.id,
        { name: m.name, lat: m.center.lat, lng: m.center.lng, province: m.province },
        m.path
      );
    },
    [handlePickComune]
  );

  return (
    <FilterPopup open={open} onClose={handleClose} title="Località" anchorEl={anchorEl}>
      <div className={styles.container}>
        {/* Preference-sync / migration notices (T011 kinds: migration,
            needs-confirmation, location-unavailable, conflict, ambiguous,
            rejected) — these had no rendering surface until now. */}
        {prefsSyncNotice && (
          <div className={styles.notice} role="status">
            <span>{prefsSyncNotice.message}</span>
            <button
              type="button"
              className={styles.noticeDismiss}
              onClick={dismissPrefsSyncNotice}
              aria-label="Chiudi avviso"
            >
              ✕
            </button>
          </div>
        )}

        {/* Classified migration outcome: identity is NULL and nothing is
            activated until the user picks a comune. The radius shown is the
            INDEPENDENT saved radius (e.g. 50 from the migration), never the
            old popup default 25 (plan l.154). */}
        {v2 && locationNeedsConfirmation && (
          <div className={styles.confirmBanner} role="status">
            Non riusciamo a identificare la tua localit&agrave; salvata: scegli il tuo comune qui
            sotto. Il raggio resta a {draftRadius} km.
          </div>
        )}

        {/* "Tutta Italia" canonical reset — always visible at top */}
        <button type="button" className={styles.tuttaItaliaBtn} onClick={handleTuttaItalia}>
          Tutta Italia
        </button>

        {/* Search input */}
        <div role="group" aria-label="Cerca città">
          {!registryLoading && !registryError && allCities.length > 0 && (
            <div className={styles.searchContainer}>
              <svg
                className={styles.searchIcon}
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="7" cy="7" r="5" />
                <line x1="11" y1="11" x2="14.5" y2="14.5" />
              </svg>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Cerca città..."
                value={citySearch}
                onChange={(e) => {
                  combobox.onQueryChange();
                  setCitySearch(e.target.value);
                }}
                onKeyDown={combobox.onKeyDown}
                autoComplete="off"
                role="combobox"
                aria-expanded={combobox.open}
                aria-controls={suggestionsListId}
                aria-activedescendant={combobox.activeDescendant}
                aria-autocomplete="list"
              />
              {citySearch && (
                <button
                  className={styles.searchClear}
                  onClick={() => setCitySearch('')}
                  type="button"
                  aria-label="Cancella ricerca"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="3" y1="3" x2="11" y2="11" />
                    <line x1="11" y1="3" x2="3" y2="11" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Autocomplete suggestions dropdown (S10/S12 live in the shared list) */}
          <ComuneSuggestionList
            listId={suggestionsListId}
            label="Città — risultati"
            combobox={combobox}
            options={suggestions}
            onSelect={handleSelectCity}
            classes={styles}
          />

          {!v2 && atCityLimit && citySearch.trim().length >= 2 && (
            <p className={styles.gpsError} role="status">
              Puoi selezionare al massimo {MAX_SELECTED_CITIES} città. Rimuovine una per aggiungerne
              un&apos;altra.
            </p>
          )}

          {registryLoading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} aria-label="Caricamento..." />
              <span>Caricamento...</span>
            </div>
          )}

          {registryError && !registryLoading && (
            <div className={styles.errorState} role="alert">
              <span>Errore nel caricamento</span>
              <button type="button" className={styles.retryBtn} onClick={fetchCities}>
                Riprova
              </button>
            </div>
          )}

          {!registryLoading && !registryError && registryState === 'ready' && allCities.length === 0 && (
            <div className={styles.emptyState}>
              <span>Nessuna citt&agrave; disponibile</span>
            </div>
          )}
        </div>
        {/* Selected chips — v2 renders EXACTLY ONE comune (replace, not append) */}
        {v2 ? (
          v2Chip && (
            <div className={styles.selectedChips} aria-label="Comune selezionato">
              <SelectedCityChip
                name={v2Chip.name}
                province={v2Chip.province}
                onRemove={handleClearComune}
              />
            </div>
          )
        ) : (
          draftCities.length > 0 && (
            <div className={styles.selectedChips} aria-label="Città selezionate">
              {draftCities.map((city) => (
                <SelectedCityChip
                  key={city.name}
                  name={city.name}
                  province={city.province}
                  onRemove={() => handleRemoveCity(city.name)}
                />
              ))}
            </div>
          )
        )}

        <button
          type="button"
          className={`${styles.gpsBtn} ${draftGps ? styles.gpsBtnActive : ''}`}
          onClick={() => void handleGpsClick()}
          disabled={gpsLoading}
        >
          <LocationIcon />
          <span>{gpsLoading ? 'Rilevamento...' : 'Posizione attuale'}</span>
        </button>

        {gpsError && (
          <p className={styles.gpsError} role="alert">
            {gpsError}
          </p>
        )}

        {/* v2 manual recovery after an uncertain placement: real candidates,
            never a nearest-guess substitution. W1c: NATIVE buttons, so Tab +
            Enter/Space work without any ARIA gymnastics (a listbox would need
            its own focus/active-descendant machinery — the review accepts
            `type="button"` chips for this one-off list). */}
        {gpsCandidates.length > 0 && (
          <ul className={styles.suggestionsDropdown} aria-label="Comuni possibili">
            {gpsCandidates.map((candidate) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  className={styles.candidateBtn}
                  onClick={() => handlePickCandidate(candidate)}
                >
                  <span>{candidate.name}</span>
                  <span className={styles.suggestionProvince}>({candidate.province})</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.divider} />

        <div>
          <div className={styles.radiusHeader}>
            <span className={styles.radiusLabel}>Raggio</span>
            <span className={styles.radiusValue}>{draftRadius} km</span>
          </div>

          <input
            type="range"
            className={styles.slider}
            min={5}
            max={200}
            step={5}
            value={draftRadius}
            onChange={handleRadiusChange}
            aria-label={`Raggio: ${draftRadius} chilometri`}
            style={
              {
                '--slider-fill': `${sliderFillPercent}%`,
              } as React.CSSProperties
            }
          />

          <div className={styles.sliderLabels}>
            <span>5 km</span>
            <span>200 km</span>
          </div>
        </div>
      </div>
    </FilterPopup>
  );
}




